import { Directive, Input, TemplateRef, ViewContainerRef, effect, inject } from '@angular/core';
import { AuthService } from '../../modules/auth/services/auth/auth.service';
import { SessionEntitlementService } from '../services/session-entitlement/session-entitlement.service';
import {
  PERMISSION_MODULE_SLUG,
  areAnyPermissionsCoveredByEntitlements,
} from '../config/module-entitlements.config';

@Directive({
  selector: '[hasPermission]',
  standalone: true,
})
export class HasPermissionDirective {
  private readonly templateRef = inject(TemplateRef<unknown>);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly authService = inject(AuthService);
  private readonly sessionEntitlements = inject(SessionEntitlementService);

  private permissionInput: string | string[] = [];

  @Input()
  set hasPermission(value: string | string[]) {
    this.permissionInput = value;
    this.render();
  }

  constructor() {
    effect(() => {
      this.authService.userDetails();
      this.sessionEntitlements.entitlementCodes();
      this.sessionEntitlements.entitlementsResolved();
      this.render();
    });
  }

  private render(): void {
    this.viewContainer.clear();

    const permissions = this.normalizePermissions(this.permissionInput);
    if (!permissions.length) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      return;
    }

    const hasRbac = this.authService.hasPermission(permissions);
    const hasPlanAccess =
      this.authService.hasFullPortalAccess() &&
      areAnyPermissionsCoveredByEntitlements(
        permissions,
        (slug) => this.sessionEntitlements.canAccessModule(slug),
        this.sessionEntitlements.entitlementsResolved(),
      );

    if (!hasRbac && !hasPlanAccess) {
      return;
    }

    if (!this.passesEntitlementGate(permissions)) {
      return;
    }

    this.viewContainer.createEmbeddedView(this.templateRef);
  }

  private normalizePermissions(value: string | string[]): string[] {
    if (!value) {
      return [];
    }
    return (Array.isArray(value) ? value : [value]).filter(Boolean);
  }

  private passesEntitlementGate(permissions: string | string[]): boolean {
    if (!this.authService.hasFullPortalAccess()) {
      return true;
    }
    if (!this.sessionEntitlements.entitlementsResolved()) {
      return true;
    }
    const list = Array.isArray(permissions) ? permissions : [permissions];
    return list.every((permission) => {
      const slug = PERMISSION_MODULE_SLUG[permission];
      if (!slug) {
        return true;
      }
      return this.sessionEntitlements.canAccessModule(slug);
    });
  }
}
